import { useEffect, useReducer, useRef, useState } from 'react';
import { useGameStore } from '../../../store/gameStore';
import { useGameLoop } from '../../../hooks/useGameLoop';
import { useKeyboard } from '../../../hooks/useKeyboard';
import { ScaleToFit } from '../ScaleToFit';
import { playPellet, playDive, playGhostCatch } from '../../../utils/sounds';

// 0=empty, 1=wall, 2=pellet, 3=portal
const COLS = 15;
const ROWS = 10;
const CELL = 40; // px
const MOVE_DELAY = 150; // ms between player moves
const CAUGHT_FLASH_MS = 1200; // ms to show caught notification

function getGhostDelay(runCount: number): number {
  if (runCount >= 7) return 170;
  if (runCount >= 5) return 220;
  if (runCount >= 3) return 280;
  return 350;
}

// Recursive backtracker maze generator.
// Logical cells occupy odd row/col positions; walls between them are at even positions.
function generateMaze(): number[][] {
  const grid: number[][] = Array.from({ length: ROWS }, () =>
    new Array<number>(COLS).fill(1),
  );

  const logRows = Math.floor((ROWS - 1) / 2); // 4
  const logCols = Math.floor((COLS - 1) / 2); // 7
  const visited: boolean[][] = Array.from({ length: logRows }, () =>
    new Array<boolean>(logCols).fill(false),
  );

  function carve(lr: number, lc: number): void {
    visited[lr]![lc] = true;
    grid[lr * 2 + 1]![lc * 2 + 1] = 0;

    const dirs = ([[0, 1], [0, -1], [1, 0], [-1, 0]] as [number, number][]).sort(
      () => Math.random() - 0.5,
    );
    for (const [dr, dc] of dirs) {
      const nlr = lr + dr;
      const nlc = lc + dc;
      if (nlr >= 0 && nlr < logRows && nlc >= 0 && nlc < logCols && !visited[nlr]![nlc]) {
        grid[lr * 2 + 1 + dr]![lc * 2 + 1 + dc] = 0; // carve connecting wall
        carve(nlr, nlc);
      }
    }
  }

  carve(0, 0); // start from top-left logical cell → grid[1][1]

  // Portal at the far corner from start
  const portalR = logRows * 2 - 1; // 7
  const portalC = logCols * 2 - 1; // 13
  grid[portalR]![portalC] = 3;

  // Pellets at every logical cell position except player start and portal
  for (let lr = 0; lr < logRows; lr++) {
    for (let lc = 0; lc < logCols; lc++) {
      const r = lr * 2 + 1;
      const c = lc * 2 + 1;
      if (grid[r]![c] === 0 && !(r === 1 && c === 1)) {
        grid[r]![c] = 2;
      }
    }
  }

  // Pellets on carved passage cells (between logical nodes)
  for (let r = 1; r < ROWS - 1; r++) {
    for (let c = 1; c < COLS - 1; c++) {
      if (grid[r]![c] === 0 && (r % 2 === 0 || c % 2 === 0)) {
        grid[r]![c] = 2;
      }
    }
  }

  return grid;
}

function countPellets(grid: number[][]): number {
  return grid.flat().filter((v) => v === 2).length;
}

// BFS from ghost to target; returns the first step to take, or null if unreachable.
function bfsNextStep(
  ghost: { row: number; col: number },
  target: { row: number; col: number },
  grid: number[][],
): { row: number; col: number } | null {
  type Node = { row: number; col: number; first: { row: number; col: number } | null };
  const queue: Node[] = [{ row: ghost.row, col: ghost.col, first: null }];
  const visited = new Set<string>([`${ghost.row},${ghost.col}`]);

  while (queue.length > 0) {
    const cur = queue.shift()!;
    for (const [dr, dc] of [[0, 1], [0, -1], [1, 0], [-1, 0]] as [number, number][]) {
      const nr = cur.row + dr;
      const nc = cur.col + dc;
      const key = `${nr},${nc}`;
      if (visited.has(key)) continue;
      const cell = grid[nr]?.[nc];
      if (cell === undefined || cell === 1) continue;
      visited.add(key);
      const first = cur.first ?? { row: nr, col: nc };
      if (nr === target.row && nc === target.col) return first;
      queue.push({ row: nr, col: nc, first });
    }
  }
  return null;
}

interface Props {
  active: boolean;
}

export function OuterWorld({ active }: Props) {
  const diveIn = useGameStore((s) => s.diveIn);
  const addScore = useGameStore((s) => s.addScore);
  const phase = useGameStore((s) => s.phase);
  const runCount = useGameStore((s) => s.runCount);
  const ghostDelay = getGhostDelay(runCount);
  const keys = useKeyboard();
  const [, rerender] = useReducer((x: number) => x + 1, 0);

  const [mazeData] = useState(() => {
    const maze = generateMaze();
    return { maze, totalPellets: countPellets(maze) };
  });
  const playerRef = useRef({ row: 1, col: 1 }); // top-left logical cell
  const gridRef = useRef(mazeData.maze.map((r) => [...r]));
  const pelletsRef = useRef(mazeData.totalPellets);
  const { totalPellets } = mazeData;
  const lastMoveRef = useRef(0);
  const divingRef = useRef(false);

  // Ghost state — starts near bottom-right (logical cell 3,3 → grid[7][7])
  const ghostRef = useRef({ row: 7, col: 7 });
  const ghostTimerRef = useRef(0);
  const caughtRef = useRef(false);
  const caughtTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Snapshot of initial pellet positions for resetting after a ghost catch
  const initialPelletsRef = useRef<{ row: number; col: number }[]>(
    mazeData.maze.flatMap((row, r) =>
      row.flatMap((cell, c) => (cell === 2 ? [{ row: r, col: c }] : [])),
    ),
  );

  useEffect(() => {
    if (active) divingRef.current = false;
  }, [active]);

  useEffect(() => {
    return () => {
      if (caughtTimerRef.current) clearTimeout(caughtTimerRef.current);
    };
  }, []);

  useGameLoop(
    () => {
      if (divingRef.current) return;
      const now = performance.now();

      // --- Ghost movement ---
      if (now - ghostTimerRef.current >= ghostDelay) {
        ghostTimerRef.current = now;
        const step = bfsNextStep(ghostRef.current, playerRef.current, gridRef.current);
        if (step) ghostRef.current = step;

        // Ghost catches player
        if (
          !caughtRef.current &&
          ghostRef.current.row === playerRef.current.row &&
          ghostRef.current.col === playerRef.current.col
        ) {
          caughtRef.current = true;
          playGhostCatch();

          // Reset all pellets in the grid
          for (const pos of initialPelletsRef.current) {
            if (gridRef.current[pos.row]![pos.col] === 0) {
              gridRef.current[pos.row]![pos.col] = 2;
            }
          }
          pelletsRef.current = countPellets(gridRef.current);

          // Move ghost back to starting position so it doesn't instantly catch again
          ghostRef.current = { row: 7, col: 7 };

          if (caughtTimerRef.current) clearTimeout(caughtTimerRef.current);
          caughtTimerRef.current = setTimeout(() => {
            caughtRef.current = false;
            rerender();
          }, CAUGHT_FLASH_MS);

          rerender();
          return;
        }
      }

      // --- Player movement ---
      if (now - lastMoveRef.current < MOVE_DELAY) return;

      let dr = 0;
      let dc = 0;
      if (keys.has('ArrowUp') || keys.has('KeyW')) dr = -1;
      else if (keys.has('ArrowDown') || keys.has('KeyS')) dr = 1;
      else if (keys.has('ArrowLeft') || keys.has('KeyA')) dc = -1;
      else if (keys.has('ArrowRight') || keys.has('KeyD')) dc = 1;

      if (dr === 0 && dc === 0) return;

      const { row, col } = playerRef.current;
      const newRow = row + dr;
      const newCol = col + dc;
      const cell = gridRef.current[newRow]?.[newCol];

      if (cell === undefined || cell === 1) return;

      playerRef.current = { row: newRow, col: newCol };
      lastMoveRef.current = now;

      if (cell === 2) {
        gridRef.current[newRow]![newCol] = 0;
        pelletsRef.current -= 1;
        addScore(10);
        playPellet();
      } else if (cell === 3 && pelletsRef.current === 0) {
        divingRef.current = true;
        playDive();
        diveIn();
      }

      rerender();
    },
    active && phase === 'playing',
  );

  const { row: pr, col: pc } = playerRef.current;
  const { row: gr, col: gc } = ghostRef.current;

  const grid = gridRef.current;
  const pelletsLeft = pelletsRef.current;
  const isCaught = caughtRef.current;

  return (
    <div className="flex flex-col items-center gap-4 p-4">
      <div className="text-sm text-gray-400 font-mono">
        Pellets: {totalPellets - pelletsLeft}/{totalPellets}
        {pelletsLeft === 0 && (
          <span className="ml-3 text-yellow-400 animate-pulse">★ Portal ready!</span>
        )}
      </div>
      {isCaught && (
        <div className="text-red-400 font-bold text-sm animate-pulse">
          ☠ Ghost caught you! Pellets reset!
        </div>
      )}
      <ScaleToFit nativeWidth={COLS * CELL} nativeHeight={ROWS * CELL}>
      <div
        className={`border rounded relative transition-colors duration-200 ${isCaught ? 'border-red-700' : 'border-gray-700'}`}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${COLS}, ${CELL}px)`,
        }}
      >
        {grid.flatMap((row, r) =>
          row.map((cell, c) => {
            const isPlayer = pr === r && pc === c;
            const isGhost = gr === r && gc === c;
            const key = `${r}-${c}`;

            if (isPlayer) {
              return (
                <div
                  key={key}
                  style={{ width: CELL, height: CELL }}
                  className={`flex items-center justify-center ${isCaught ? 'bg-red-900' : 'bg-cyan-900'}`}
                >
                  <span className={`font-bold text-lg leading-none ${isCaught ? 'text-red-300' : 'text-cyan-300'}`}>
                    @
                  </span>
                </div>
              );
            }

            if (isGhost) {
              return (
                <div
                  key={key}
                  style={{ width: CELL, height: CELL }}
                  className="flex items-center justify-center bg-red-950"
                >
                  <span className="text-red-400 font-bold text-lg leading-none animate-pulse">ψ</span>
                </div>
              );
            }

            if (cell === 1) {
              return (
                <div
                  key={key}
                  style={{ width: CELL, height: CELL }}
                  className="bg-gray-800"
                />
              );
            }

            if (cell === 2) {
              return (
                <div
                  key={key}
                  style={{ width: CELL, height: CELL }}
                  className="flex items-center justify-center bg-gray-900"
                >
                  <div className="w-2 h-2 rounded-full bg-yellow-500" />
                </div>
              );
            }

            if (cell === 3) {
              return (
                <div
                  key={key}
                  style={{ width: CELL, height: CELL }}
                  className="flex items-center justify-center bg-gray-900"
                >
                  <span
                    className={`text-lg leading-none ${pelletsLeft === 0 ? 'text-yellow-400 animate-pulse' : 'text-gray-600'}`}
                  >
                    ★
                  </span>
                </div>
              );
            }

            return (
              <div
                key={key}
                style={{ width: CELL, height: CELL }}
                className="bg-gray-900"
              />
            );
          }),
        )}
      </div>
      </ScaleToFit>
      <p className="text-xs text-gray-600">Collect all pellets, then enter the portal ★ — avoid the ghost ψ!</p>
    </div>
  );
}
